/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button, Dialog } from 'alurkerja-ui';
import React, { useEffect, useRef } from 'react';
import { FormField, IFormField } from './FormField';
import { MinioService } from '@/api/minio';
import { ControllerRenderProps } from 'react-hook-form';
import { downloadFile } from '@/utils/libs/fileUtil';
import { env } from '@/utils/configs/env';
import { FileUpload } from 'primereact/fileupload';
import { Tag } from 'primereact/tag';
import {} from 'react-icons/fa6';
import { FaTimes, FaDownload } from 'react-icons/fa';

// type ResponseValue = {
//   id: string;
//   name: string;
//   path: string;
//   size: number;
//   objectURL?: string;
//   type?: string;
// };

interface UploadFieldProps extends IFormField {
  type?: 'image' | 'file' | 'xlsx';
  isMultiple?: boolean;
  allowedExtensions?: string[];
}

interface ExtendedUploadFieldProps extends UploadFieldProps {
  field: ControllerRenderProps;
}

const FileItemTemplate: React.FC<{
  file: any;
  fileProps: any;
  type?: 'image' | 'file' | 'xlsx';
  props: ExtendedUploadFieldProps;
  onDownload: (filename: string, path: string) => void;
}> = ({ file, fileProps, type, onDownload, props }) => {
  return (
    <div className="flex flex-row items-center justify-start">
      <div className="flex align-items-center" style={{ width: '40%' }}>
        {(type?.includes('image') || type === 'file') && (
          <img
            role="presentation"
            src={file.objectURL ?? file.type}
            width={150}
          />
        )}
      </div>

      <div className="flex flex-col items-start gap-2">
        <span className="flex text-left flex-column">{file.name} tes</span>

        <Tag
          value={fileProps.formatSize}
          severity="warning"
          className="px-3 py-2"
        />
      </div>

      <div className="flex flex-row gap-2 ml-auto">
        <Button
          type="button"
          variant="outlined"
          className="text-blue-400 border-blue-400"
          onClick={() => onDownload(file.name, file.objectURL ?? file.type)}
        >
          <FaDownload />
        </Button>

        <Button
          type="button"
          variant="outlined"
          className="text-red-400 border-red-400"
          onClick={() => {
            const existingFiles = props.field.value;
            if (existingFiles) {
              const newFiles = existingFiles.filter((e: any) => {
                if (e.objectURL && file.objectURL) {
                  return e.objectURL !== file.objectURL;
                }
                return e.name !== file.name;
              });
              props.field.onChange(newFiles);
            }
            fileProps.onRemove();
          }}
        >
          <FaTimes />
        </Button>
      </div>
    </div>
  );
};

const ExtendedUploadField: React.FC<ExtendedUploadFieldProps> = props => {
  const ref = useRef<FileUpload>(null);
  const { mutate } = MinioService.useDownloadFile();

  const handlePaste = (event: ClipboardEvent) => {
    const clipboardItems = event.clipboardData?.items;
    if (!clipboardItems) return;

    const images = Array.from(clipboardItems).filter(item =>
      item.type.startsWith('image/'),
    );

    if (images.length > 0) {
      const files = images
        .map(item => item.getAsFile())
        .filter(file => file !== null)
        .map(file => {
          const newFile = new File([file!], file!.name, { type: file!.type });
          (newFile as any).objectURL = URL.createObjectURL(file!);
          return newFile;
        });

      ref.current?.getFiles();

      props.field.onChange([...(props.field.value || []), ...files]);
    }
  };

  useEffect(() => {
    if (props.field.value && Array.isArray(props.field.value)) {
      const values = props.field.value as any[];
      const files = values.map(e => {
        if (!e.objectURL) {
          const array = new Uint8Array(e.size);
          const url = env.apiBaseUrl + '/download' + e.path;
          array.fill(0);

          return new globalThis.File([array], e.name, {
            type: url,
          });
        }
        return e;
      });

      ref.current?.setFiles(files);
    }

    // Tambahkan event listener untuk menangkap paste
    const pasteListener = (event: ClipboardEvent) => handlePaste(event);
    document.addEventListener('paste', pasteListener);

    // Jika component dihapus, hapus event listener
    return () => {
      document.removeEventListener('paste', pasteListener);
    };
  }, [ref, props.field.value]);

  const downloadAction = (filename: string, path: string) => {
    mutate(path, {
      onSuccess: data => downloadFile(data, filename),
      onError: () => {
        Dialog.error({
          title: 'Error',
          description: 'Failed to download attachment',
        });
      },
    });
  };

  const unsubmittedDownloads = async (fileName: string, fileUrl: string) => {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      Dialog.error({
        title: 'Error',
        description: 'Failed to download attachment',
      });
    }
  };

  return (
    <FileUpload
      ref={ref}
      multiple={props.isMultiple ?? false}
      className="border border-gray-300 rounded-md"
      customUpload
      maxFileSize={1024 * 1024 * 2}
      mode="advanced"
      uploadOptions={{ className: 'hidden' }}
      cancelOptions={{ className: 'hidden' }}
      itemTemplate={(file, fileProps) => {
        return (
          <FileItemTemplate
            props={props}
            file={file}
            fileProps={fileProps}
            type={props.type}
            onDownload={(filename, path) => {
              if (path.includes('blob:')) {
                unsubmittedDownloads(filename, path);
              } else {
                downloadAction(filename, path);
              }
            }}
          />
        );
      }}
      emptyTemplate={
        <p className="m-0">
          Drag and drop files or paste images here (Ctrl+V).
        </p>
      }
      onSelect={e => {
        const rawFilesCount = e.files.length;

        if (props.type === 'image') {
          const imageExtensions = ['.jpg', '.jpeg', '.png'];
          e.files = e.files.filter((file: any) => {
            const fileExtension = file.name
              .slice(file.name.lastIndexOf('.'))
              .toLowerCase();
            return imageExtensions.includes(fileExtension);
          });

          if (rawFilesCount !== e.files.length) {
            Dialog.error({
              title: 'Error',
              description: 'Only image files are allowed',
            });
          }
        }

        props.field.onChange(e.files);
      }}
      accept={(() => {
        if (props.type === 'image') {
          return 'image/*';
        }
        return undefined;
      })()}
    />
  );
};

export const UploadField: React.FC<UploadFieldProps> = props => {
  return (
    <FormField
      {...props}
      render={({ field }) => <ExtendedUploadField {...props} field={field} />}
    />
  );
};
